import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router';
import type { DuplicateCandidate, Session } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { RequestForm } from '@/features/requests/request-form';
import { useProducts, useSameProblem, useSubmitRequest } from '@/hooks/use-requests';

/** « M'abonner et ajouter mon contexte » (ADR-009): same workflow as « C'est le même problème » on screen 16. */
function SubscribeAction({ duplicate, join, busy }: { duplicate: DuplicateCandidate; join: () => void; busy: boolean }) {
  if (duplicate.isMine) {
    return (
      <Link to={`/demandes/${duplicate.id}`} className="text-[13px] font-bold no-underline">
        Votre demande
      </Link>
    );
  }
  if (duplicate.isSubscribed) {
    return (
      <Link to={`/demandes/${duplicate.id}`} className="text-[13px] font-bold text-status-delivered-foreground no-underline">
        Vous suivez cette demande
      </Link>
    );
  }
  return (
    <Button type="button" variant="outline" size="sm" className="self-start" disabled={busy} onClick={join}>
      M'abonner et ajouter mon contexte
    </Button>
  );
}

const NEXT_STEPS: { label: string; text: string }[] = [
  { label: 'Soumise', text: "la demande est créée dans le backlog de l'équipe produit." },
  { label: 'Qualifiée', text: "sous 2 jours ouvrés, l'équipe confirme le besoin ou vous pose une question." },
  // Mock-up wording kept as is; it conflicts with « le référent consulte, il n'arbitre pas » (see CLAUDE.md, « Écarts »).
  { label: 'Planifiée', text: "un bug est pris dans le sprint ; une évolution passe par l'arbitrage de votre référent." },
  { label: 'Livrée', text: 'vous êtes prévenu et la release note est publiée.' },
];

/** 02 · Nouvelle demande. No priority field: the priority belongs to the PO. */
export function NewRequestPage() {
  const session = useOutletContext<Session>();
  const navigate = useNavigate();
  const products = useProducts();
  const submittable = useMemo(() => (products.data ?? []).filter((p) => p.canSubmit), [products.data]);
  const submit = useSubmitRequest();
  const sameProblem = useSameProblem();
  // ADR-005 §6: the user's teams with access to the product; without product, all its teams.
  const teamsFor = useCallback(
    (productId: string | null) => (productId ? (submittable.find((p) => p.id === productId)?.eligibleTeams ?? []) : session.teams),
    [submittable, session.teams],
  );

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Link to="/" className="text-[13px] no-underline">
          ← Retour à l'accueil
        </Link>
        <h1 className="m-0 text-[26px] font-black tracking-[-0.01em]">Nouvelle demande</h1>
        <p className="m-0 text-sm text-muted-foreground">
          Votre demande est transmise à l'équipe produit et qualifiée sous 2 jours ouvrés. Vous êtes notifié à chaque changement de statut.
        </p>
      </div>

      <RequestForm
        products={submittable}
        teamsFor={teamsFor}
        impactLabel="Impact pour votre équipe"
        descriptionHint="Ce que vous faisiez, ce qui s'est passé, ce que vous attendiez. Les étapes pour reproduire aident l'équipe à qualifier plus vite."
        duplicateIntro="Si l'une correspond, abonnez-vous : votre description y sera ajoutée."
        duplicateAction={(d, join, busy) => <SubscribeAction duplicate={d} join={join} busy={busy} />}
        promptOnSubmit
        cancelTo="/"
        onSubmit={async (input, files) => {
          const created = await submit.mutateAsync({ input, files });
          navigate(`/demandes/${created.id}`);
        }}
        onSameProblem={async (candidate, input, files, place) => {
          // No request is created: the user now follows the existing one, where its context was added.
          const joined = await sameProblem.mutateAsync({ candidateId: candidate.id, place, input, files });
          navigate(`/demandes/${joined.id}`);
        }}
        renderActions={(send, submitting) => (
          <Button type="button" className="min-h-11" disabled={submitting} onClick={() => send('create')}>
            Envoyer la demande
          </Button>
        )}
        aside={
          <section className="flex flex-col gap-3.5 rounded-card border border-border bg-card px-5 py-4">
            <h2 className="m-0 text-[15px] font-bold">Ce qui se passe ensuite</h2>
            <ol className="m-0 flex list-none flex-col gap-3 p-0">
              {NEXT_STEPS.map((step, i) => (
                <li key={step.label} className="flex items-start gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-strong">{i + 1}</span>
                  <span className="text-[13px] text-foreground-secondary">
                    <strong className="font-bold text-foreground">{step.label}</strong> — {step.text}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        }
      />
    </>
  );
}
